<template>
  <div class="page">
    <div class="seiten-kopf">
      <router-link to="/rezepte" class="seiten-kopf-zurueck" aria-label="Zurück">
        <font-awesome-icon icon="fa-solid fa-arrow-left" />
      </router-link>
      <h1>Rezepte Liste</h1>
    </div>

    <p v-if="Fehlermeldung" class="error-message">{{ Fehlermeldung }}</p>

    <p v-if="Rezepte.length === 0" class="hint-message">Noch keine Rezepte vorhanden.</p>

    <template v-else>
      <div class="liste-suche">
        <input v-model="Suche" type="text" placeholder="Suchen..." class="liste-suche-feld" />
        <button
          type="button"
          class="filter-btn"
          :class="{ 'filter-btn--aktiv': FilterAnzahl > 0 }"
          aria-label="Filtern"
          title="Filtern"
          @click="FilterOeffnen"
        >
          <font-awesome-icon icon="fa-solid fa-filter" />
          <span v-if="FilterAnzahl > 0" class="filter-btn-badge">{{ FilterAnzahl }}</span>
        </button>
        <button type="button" class="btn btn-secondary liste-falten-btn" @click="FoldUnfoldAlle">
          {{ AlleEingeklappt ? 'Ausklappen' : 'Einklappen' }}
        </button>
      </div>
      <p v-if="Gruppen.length === 0" class="hint-message">Keine Einträge gefunden.</p>
      <div v-else class="eintrag-liste-scroll">
        <div class="kategorie-gruppe" v-for="Gruppe in Gruppen" :key="Gruppe.Kategorie">
          <h2 class="kategorie-titel" @click="KategorieToggeln(Gruppe.Kategorie)">
            <span>{{ Gruppe.Kategorie }}</span>
            <span class="kategorie-badge">{{ Gruppe.Eintraege.length }}</span>
            <font-awesome-icon icon="fa-solid fa-chevron-right" class="kategorie-pfeil" :class="{ 'kategorie-pfeil--offen': !GeklapptKategorien.includes(Gruppe.Kategorie) }" />
          </h2>
          <div v-show="!GeklapptKategorien.includes(Gruppe.Kategorie)" class="eintrag-liste">
            <div class="eintrag-zeile" v-for="Rezept in Gruppe.Eintraege" :key="Rezept.Id">
              <div class="eintrag-mit-bild">
                <img
                  v-if="Rezept.HatBild"
                  :src="RezeptBildUrl(Rezept)"
                  class="mini-bild"
                  alt=""
                  loading="lazy"
                  @error="$event.target.style.display = 'none'"
                />
                <div class="eintrag-info">
                  <span class="eintrag-name">
                    {{ Rezept.Name }}
                    <span
                      v-if="Rezept.PlanungAnzahl > 0"
                      class="ranking-badge"
                      :title="`${Rezept.PlanungAnzahl}× verplant`"
                    >
                      <font-awesome-icon icon="fa-solid fa-fire" />
                      {{ Rezept.PlanungAnzahl }}×
                    </span>
                  </span>
                  <span class="eintrag-details">{{ ZutatenText(Rezept) }}</span>
                </div>
              </div>
              <div class="eintrag-aktionen">
                <router-link :to="`/rezepte/bearbeiten/${Rezept.Id}`" class="aktion-button">Bearbeiten</router-link>
                <button type="button" class="aktion-button loeschen" @click="Loeschen(Rezept)">Rezept löschen</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Filtern -->
    <Teleport to="body">
      <div v-if="FilterOffen" class="overlay" @click.self="FilterOffen = false">
        <div class="bottom-sheet" role="dialog" aria-modal="true" aria-label="Rezepte filtern">
          <div class="sheet-griff"></div>
          <h3 class="sheet-titel">Rezepte filtern</h3>

          <div class="filter-abschnitt">
            <span class="filter-abschnitt-titel">Kategorie</span>
            <div class="filter-chip-reihe">
              <button
                v-for="Kategorie in KategorieReihenfolge"
                :key="Kategorie"
                type="button"
                class="filter-chip"
                :class="{ 'filter-chip--aktiv': FilterKategorien.includes(Kategorie) }"
                @click="FilterKategorieToggeln(Kategorie)"
              >
                {{ Kategorie }}
              </button>
            </div>
          </div>

          <div class="filter-abschnitt">
            <span class="filter-abschnitt-titel">Enthält Zutat</span>
            <input
              v-model="FilterZutatSuche"
              type="text"
              placeholder="Zutat suchen..."
              class="liste-suche-feld filter-zutat-suche"
            />
            <div v-if="Zutaten.length === 0" class="hint-message">Noch keine Zutaten vorhanden.</div>
            <div v-else class="kacheln-grid">
              <button
                v-for="Zutat in GefilterteFilterZutaten"
                :key="Zutat.Id"
                type="button"
                class="kachel"
                :class="{ 'kachel--ausgewaehlt': FilterZutatIds.includes(Zutat.Id) }"
                @click="FilterZutatToggeln(Zutat.Id)"
              >
                <font-awesome-icon
                  v-if="FilterZutatIds.includes(Zutat.Id)"
                  icon="fa-solid fa-check"
                  class="kachel-check"
                />
                {{ Zutat.Name }}
              </button>
              <p v-if="GefilterteFilterZutaten.length === 0" class="hint-message">Keine Zutat gefunden.</p>
            </div>
          </div>

          <label class="filter-toggle-zeile">
            <input v-model="FilterNurMitBild" type="checkbox" />
            Nur Rezepte mit Foto
          </label>

          <div class="filter-abschnitt">
            <span class="filter-abschnitt-titel">Mindestens X Personen</span>
            <div class="input-field filter-personen-feld">
              <font-awesome-icon icon="fa-solid fa-users" />
              <input v-model.number="FilterMindPersonen" type="number" min="1" max="99" placeholder="z.B. 4" />
            </div>
          </div>

          <button v-if="FilterAnzahl > 0" type="button" class="filter-zuruecksetzen" @click="FilterZuruecksetzen">
            Filter zurücksetzen ({{ FilterAnzahl }})
          </button>

          <button type="button" class="btn btn-primary" @click="FilterOffen = false">Fertig</button>
        </div>
      </div>
    </Teleport>

  </div>
</template>

<script>
import { Rezepte, RezepteLaden, RezeptLoeschen } from '../composables/useRezepte'
import { Zutaten, ZutatenLaden, ZutatFinden } from '../composables/useZutaten'
import { BildUrl } from '../composables/useBilder'

const KategorieReihenfolge = ['Frühstück', 'Vorspeise', 'Hauptgang', 'Nachspeise', 'Abendessen']

export default {
  name: 'RezepteListeView',
  data() {
    return {
      Rezepte,
      Zutaten,
      KategorieReihenfolge,
      Fehlermeldung: '',
      Suche: '',
      GeklapptKategorien: [],
      FilterOffen: false,
      FilterKategorien: [],
      FilterZutatIds: [],
      FilterZutatSuche: '',
      FilterNurMitBild: false,
      FilterMindPersonen: null
    }
  },
  async created() {
    await Promise.all([ZutatenLaden(), RezepteLaden()])
  },
  computed: {
    GefilterteRezepte() {
      const s = this.Suche.toLowerCase().trim()
      return this.Rezepte
        .filter(r => !s || r.Name.toLowerCase().includes(s))
        .filter(r => !this.FilterNurMitBild || r.HatBild)
        .filter(r => !this.FilterMindPersonen || (r.Personen || 0) >= this.FilterMindPersonen)
        .filter(r => this.FilterZutatIds.length === 0 || (r.Zutaten || []).some(z => this.FilterZutatIds.includes(z.ZutatId)))
    },
    FilterAnzahl() {
      return this.FilterKategorien.length
        + this.FilterZutatIds.length
        + (this.FilterNurMitBild ? 1 : 0)
        + (this.FilterMindPersonen ? 1 : 0)
    },
    GefilterteFilterZutaten() {
      const s = this.FilterZutatSuche.toLowerCase().trim()
      if (!s) return this.Zutaten
      return this.Zutaten.filter(z => z.Name.toLowerCase().includes(s))
    },
    AlleEingeklappt() {
      return this.Gruppen.length > 0 && this.GeklapptKategorien.length >= this.Gruppen.length
    },
    Gruppen() {
      // Kategorie-Filter bestimmt, welche Kategorie-Gruppen ueberhaupt betrachtet werden
      // (leer = alle, wie bisher); alle anderen Filter wirken bereits in GefilterteRezepte.
      const AktiveKategorien = this.FilterKategorien.length === 0
        ? KategorieReihenfolge
        : KategorieReihenfolge.filter(K => this.FilterKategorien.includes(K))

      return AktiveKategorien
        .map(Kategorie => ({
          Kategorie,
          // Ranking: am häufigsten verplante Rezepte zuerst, bei Gleichstand alphabetisch
          // Ein Rezept mit mehreren Kategorien erscheint in jeder zutreffenden Gruppe.
          Eintraege: this.GefilterteRezepte
            .filter(Rezept => (Rezept.Kategorien || []).includes(Kategorie))
            .slice()
            .sort((a, b) => (b.PlanungAnzahl || 0) - (a.PlanungAnzahl || 0) || a.Name.localeCompare(b.Name))
        }))
        .filter(Gruppe => Gruppe.Eintraege.length > 0)
    }
  },
  methods: {
    KategorieToggeln(Name) {
      const Index = this.GeklapptKategorien.indexOf(Name)
      if (Index === -1) {
        this.GeklapptKategorien.push(Name)
      } else {
        this.GeklapptKategorien.splice(Index, 1)
      }
    },
    FoldUnfoldAlle() {
      if (this.AlleEingeklappt) {
        this.GeklapptKategorien = []
      } else {
        this.GeklapptKategorien = this.Gruppen.map(g => g.Kategorie)
      }
    },
    FilterOeffnen() {
      this.FilterZutatSuche = ''
      this.FilterOffen = true
    },
    FilterKategorieToggeln(Kategorie) {
      const Index = this.FilterKategorien.indexOf(Kategorie)
      if (Index === -1) {
        this.FilterKategorien.push(Kategorie)
      } else {
        this.FilterKategorien.splice(Index, 1)
      }
    },
    FilterZutatToggeln(ZutatId) {
      const Index = this.FilterZutatIds.indexOf(ZutatId)
      if (Index === -1) {
        this.FilterZutatIds.push(ZutatId)
      } else {
        this.FilterZutatIds.splice(Index, 1)
      }
    },
    FilterZuruecksetzen() {
      this.FilterKategorien = []
      this.FilterZutatIds = []
      this.FilterNurMitBild = false
      this.FilterMindPersonen = null
    },
    RezeptBildUrl(Rezept) {
      return BildUrl('Rezept', Rezept.Id, Rezept.BildVersion)
    },
    ZutatenText(Rezept) {
      const Personen = Rezept.Personen
        ? `Für ${Rezept.Personen} ${Rezept.Personen === 1 ? 'Person' : 'Personen'}`
        : ''
      if (!Rezept.Zutaten || Rezept.Zutaten.length === 0) {
        return [Personen, 'Keine Zutaten hinterlegt'].filter(Boolean).join(' · ')
      }
      // Name immer live über ZutatId aus dem Zutaten-Katalog ableiten, nicht aus der
      // beim Anlegen/Bearbeiten lokal gespeicherten Zutaten-Zeile (die hat kein Name-Feld).
      const Namen = Rezept.Zutaten
        .map(Zutat => {
          const Gefunden = ZutatFinden(Zutat.ZutatId)
          return Gefunden ? Gefunden.Name : Zutat.Name
        })
        .filter(Boolean)
        .join(', ')
      return [Personen, Namen].filter(Boolean).join(' · ')
    },
    async Loeschen(Rezept) {
      this.Fehlermeldung = ''
      if (confirm(`"${Rezept.Name}" wirklich löschen?`)) {
        try {
          await RezeptLoeschen(Rezept.Id)
        } catch (Fehler) {
          this.Fehlermeldung = Fehler.message
        }
      }
    }
  }
}
</script>

<style scoped>
.eintrag-name {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.ranking-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 9px;
  border-radius: 999px;
  background: var(--color-accent-light);
  color: var(--color-accent);
  font-size: 0.7rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.ranking-badge svg {
  font-size: 0.65rem;
}

/* ── Filter-Sheet ────────────────────────────────────── */
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(12, 12, 16, 0.45);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: flex-end;
  z-index: 200;
  animation: Einblenden 0.25s ease both;
}

.bottom-sheet {
  background: var(--glas-stark);
  -webkit-backdrop-filter: var(--glas-blur);
  backdrop-filter: var(--glas-blur);
  border: 1px solid var(--glas-rand);
  border-radius: var(--radius-xl) var(--radius-xl) 0 0;
  padding: 12px 16px 32px;
  width: 100%;
  max-height: 88dvh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
  animation: SheetHoch 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
}

@media (min-width: 768px) {
  .overlay {
    justify-content: center;
    align-items: center;
  }
  .bottom-sheet {
    border-radius: var(--radius-xl);
    max-width: 480px;
    max-height: 84dvh;
    padding: 24px;
  }
  .sheet-griff { display: none; }
}

.sheet-griff {
  width: 40px;
  height: 4px;
  background: var(--color-border);
  border-radius: 2px;
  margin: 0 auto 4px;
  flex-shrink: 0;
}

.sheet-titel {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
}

.filter-zutat-suche {
  margin-bottom: 0;
}

.kacheln-grid {
  max-height: 220px;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px;
  align-content: start;
}

.kachel {
  padding: 10px 8px;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: var(--color-bg-alt);
  color: var(--color-text);
  font-size: 0.9rem;
  cursor: pointer;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  word-break: break-word;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.kachel--ausgewaehlt {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: #fff;
}

.kachel-check {
  flex-shrink: 0;
}

.filter-personen-feld {
  max-width: 160px;
}
</style>
